import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import { Alert, Box, Button, Link, MenuItem, Stack, TextField, Typography } from '@mui/material';
import Seo from '../components/Seo';
import DirectoryLayout, { DarkCard, DirectorySearch, LightCard, ProCard, fmtChecked, useMessage } from '../components/directory/DirectoryLayout';
import directoryService from '../services/directoryService';
import { RealtorRegisterResults } from '../components/directory/RegisterResults';

const TOWNS = ['Harare', 'Bulawayo', 'Mutare', 'Gweru', 'Masvingo', 'Kwekwe', 'Ruwa', 'Chitungwiza', 'Victoria Falls'];

/**
 * Find a realtor (spec 2026-09-26 §5): Eqaya members whose agency is on the
 * Estate Agents Council register, checked every night.
 */
export default function FindRealtorPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get('q') || '');
  const town = params.get('town') || '';
  const sells = params.get('sells') || '';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { message, busy, error: msgError, clearError } = useMessage();

  const qs = params.toString();
  useEffect(() => {
    let live = true;
    setLoading(true); setError('');
    directoryService.realtors(Object.fromEntries(new URLSearchParams(qs)))
      .then((res) => { if (live) setData(res.data); })
      .catch(() => { if (live) { setData(null); setError('We could not load realtors just now. Please try again in a moment.'); } })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [qs]);

  const setParam = (patch) => setParams((prev) => {
    const next = new URLSearchParams(prev);
    Object.entries(patch).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
    return next;
  }, { replace: true });

  const items = data?.items || [];
  const where = town || 'Zimbabwe';

  return (
    <>
      <Seo title="Find a registered realtor in Zimbabwe" description="Estate agents in good standing with the Estate Agents Council of Zimbabwe, checked every night." canonicalPath="/realtors" />
      <DirectoryLayout
        eyebrow="Property · Find a realtor"
        title="Find a realtor in good standing"
        lead="Every realtor here is an Eqaya member whose Estate Agents Council registration we check every night. If a registration lapses, they come off this list the same day."
        searchRow={(
          <DirectorySearch value={q} onChange={setQ} onSubmit={() => setParam({ q: q.trim() })} placeholder="Agent or agency name">
            <TextField select size="small" label="Town" value={town} onChange={(e) => setParam({ town: e.target.value })}
              sx={{ minWidth: 170, '& .MuiInputBase-root': { height: 54, borderRadius: '10px' } }}>
              <MenuItem value="">Anywhere</MenuItem>
              {TOWNS.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Sells" value={sells} onChange={(e) => setParam({ sells: e.target.value })}
              sx={{ minWidth: 190, '& .MuiInputBase-root': { height: 54, borderRadius: '10px' } }}>
              <MenuItem value="">Houses and stands</MenuItem>
              <MenuItem value="house">Houses</MenuItem>
              <MenuItem value="stand">Stands</MenuItem>
            </TextField>
          </DirectorySearch>
        )}
        resultsTitle={`Eqaya members in ${where}`}
        resultsNote={!loading && !error ? (items.length ? 'All in good standing today' : null) : null}
        loading={loading}
        error={error || msgError}
        empty={!loading && !error && items.length === 0 ? (
          <Alert severity="info" onClose={msgError ? clearError : undefined}>
            {params.get('q') ? 'No Eqaya member matches this search yet. The Estate Agents Council register is searched below.' : 'No realtor has joined Eqaya here yet. Search an agent or agency name to check the Estate Agents Council register.'}
          </Alert>
        ) : null}
        after={<RealtorRegisterResults register={data?.register} q={params.get('q') || ''} memberCount={items.length} />}
        sidebar={(
          <>
            <DarkCard title="Before you pay a deposit">
              <Box component="ol" sx={{ m: 0, pl: 2.5, display: 'flex', flexDirection: 'column', gap: 1.25, fontSize: 15, lineHeight: 1.5, color: '#e6f4f2' }}>
                <li>Make sure the agent is registered with the Estate Agents Council.</li>
                <li>Have a conveyancer check who owns it. For a title deed, they search the deed number at the Deeds Registry. For a cession, they confirm the seller&apos;s rights with the council or developer that keeps the register.</li>
                <li>Pay into a trust account — the agency&apos;s or your conveyancer&apos;s — never to a person, never in cash.</li>
              </Box>
              <Button component={RouterLink} to="/conveyancers"
                sx={{ mt: 2, height: 44, px: 2.25, borderRadius: '10px', bgcolor: '#fff', color: '#134e4a', textTransform: 'none', fontWeight: 700, '&:hover': { bgcolor: '#f0fdfa' } }}>
                Find a conveyancer
              </Button>
            </DarkCard>
            <LightCard title="Are you a realtor?">
              <Typography sx={{ fontSize: 15, lineHeight: 1.5, color: 'text.secondary', mb: 1 }}>
                Be found by buyers at home and abroad, with a good-standing badge they can trust.
              </Typography>
              <Link component={RouterLink} to="/register?role=realtor" sx={{ fontWeight: 700 }}>Join Eqaya →</Link>
            </LightCard>
            <LightCard title="Buying from abroad?">
              <Typography sx={{ fontSize: 15, lineHeight: 1.5, color: 'text.secondary', mb: 1 }}>
                Send a verified person to see the property first — dated, GPS-tagged photos and a written report.
              </Typography>
              <Stack spacing={0.5}>
                <Link component={RouterLink} to="/buying-property" sx={{ fontWeight: 700 }}>How buying works, step by step →</Link>
                <Link component={RouterLink} to="/property-check/book" sx={{ fontWeight: 700 }}>Book a property check →</Link>
              </Stack>
            </LightCard>
          </>
        )}
        footnote="Standing is checked against the Estate Agents Council of Zimbabwe's approved-agents register. It tells you the agency may operate today; it says nothing about complaints, which the register does not publish."
      >
        {items.map((r) => (
          <ProCard
            key={r.userId}
            name={r.displayName}
            firm={r.agency}
            photo={r.photoUrl}
            chips={[
              ...((r.sells || []).length === 1 ? [r.sells[0] === 'house' ? 'Houses' : 'Stands'] : (r.sells || []).length === 2 ? ['Houses and stands'] : []),
              ...(r.yearsExperience ? [`${r.yearsExperience} year${r.yearsExperience === 1 ? '' : 's'} in the trade`] : []),
            ]}
            badge={`In good standing with the EAC · ${fmtChecked(r.standing?.checkedAt)}`}
            details={[
              r.listingCount ? `**${r.listingCount} verified ${r.listingCount === 1 ? 'home' : 'homes'} for sale` : 'No homes listed yet',
              r.towns?.length ? r.towns.join(' · ') : null,
            ]}
            primary={r.listingCount
              ? { label: 'See their homes', onClick: () => navigate(`/properties?realtor=${r.userId}`) }
              : null}
            secondary={{ label: busy === r.userId ? 'Opening…' : 'Message', onClick: () => message(r.userId), disabled: busy === r.userId }}
          />
        ))}
      </DirectoryLayout>
    </>
  );
}
